import "../../../assets/scss/billings-style.scss";

import moment from "moment";
import { useEffect, useState } from "react";
import { ConfigProvider, Skeleton, Table, TableProps } from "antd";
import { useTranslation } from "react-i18next";
import { getRentalHistory } from "../../server/User";
import { useAppSelector } from "../../store/hooks";
import { randomNumber } from "../../utility/fn";

import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utility/AuthContext";

interface RentalHistoryDataType {
  id?: number;
  user_id?: number;
  product_name?: string;
  status?: string;
  machine_id?: number;
  instance_id?: number;
  template_name?: string;
  gpu_count?: number;
  payment_id?: number;
  total_amount?: number;
  created_at?: number;
  end_at?: number;
}

export const Usage = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const userState = useAppSelector((state: any) => state.user);
  const [rentalHistoryData, setRentalHistoryData] = useState<
    RentalHistoryDataType[]
  >([]);

  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const rentalHistoryColumns: TableProps<RentalHistoryDataType>["columns"] = [
    {
      title: t("Created Date"),
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => <>{moment(value).format("YYYY-MM-DD")}</>,
    },
    {
      title: t("Usage Details"),
      dataIndex: "usage_details",
      key: "usage_details",
      render: (value, record) => (
        <div>
          <strong>
            {t("Instance Id")}: {record.instance_id}
          </strong>
          <div className="usage_details_container">
            <span>
              {t("Transaction ID")}: {record.id}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: t("Machine"),
      dataIndex: "machine",
      key: "machine",
      render: (value, record) => (
        <div>
          <strong>{record.product_name}</strong>
          <div className="usage_details_container">
            <span>
              {t("GPU Count")}: {record.gpu_count}
            </span>
            <span>
              {t("Machine ID")}: {record.machine_id}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: t("Applications"),
      dataIndex: "applications",
      key: "applications",
      render: (value, record) => (
        <>{treatTemplateName(record.template_name!)}</>
      ),
    },
    {
      title: t("ESG"),
      dataIndex: "esg",
      key: "esg",
      render: (value) => <>{randomNumber(22, 80)} kWatt</>,
    },
    {
      title: t("Cost"),
      dataIndex: "total_amount",
      key: "total_amount",
      render: (value) => <strong>USD ${Number(value).toFixed(2)}</strong>,
    },
  ];

  const treatTemplateName = (templateName: string) => {
    const baseName = templateName.replace(/-\d+$/, "");
    return baseName;
  };

  const customizeRenderEmpty = () => (
    <div style={{ textAlign: "center" }}>
      <p>{t("No Data Yet")}</p>
    </div>
  );

  const getRentalHistoryData = () => {
    setLoading(true);
    getRentalHistory(1, 20)
      .then((res: any) => {
        setRentalHistoryData([...res.data.rentals]);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (isLoggedIn) {
      setLoading(true);
      getRentalHistoryData();
    }
  }, [userState]);

  return (
    <>
      {loading ? (
        <Skeleton active paragraph />
      ) : (
        <>
          {rentalHistoryData.length !== 0 ? (
            <>
              <div className="usage-title-wrapper">
                <div className="title">
                  <h2>{t("Usage")}</h2>
                  <span>{t("usage descriptions")}</span>
                </div>
              </div>
              <ConfigProvider
                renderEmpty={customizeRenderEmpty}
                theme={{
                  components: {
                    Table: {
                      headerBg: "#F6F8FD",
                    },
                  },
                }}
              >
                <Table
                  columns={rentalHistoryColumns}
                  dataSource={rentalHistoryData}
                  showHeader={rentalHistoryData.length !== 0}
                  tableLayout="fixed"
                  pagination={{
                    position: ["bottomCenter"],
                    showSizeChanger: false,
                  }}
                  scroll={{ x: "max-content" }}
                  loading={loading}
                />
              </ConfigProvider>
            </>
          ) : (
            <div className="instance-card-none">
              <h2>{t("Usage")}</h2>
              <p className="instance-card-none-descript">{t("No Instances")}</p>
              <div
                className="secondary-btn"
                data-no-auth
                onClick={() => navigate("/")}
              >
                <span>
                  <SearchOutlined /> &nbsp;
                  {t("Explore GPU Options")}
                </span>
              </div>
              <img
                src={`/images/instances/instances_empty.png`}
                width={"1160px"}
                alt="instances"
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
